import { z } from 'zod';
import { uuid, isoTimestamp } from './common.js';
/**
 * The activity feed — a per-day, per-person log of what changed on the list.
 *
 * This is a **read-only projection** of data the app already keeps: an add is a
 * `list_items` row, and a row leaving the list is a tombstoned one — `bought`
 * when a purchase points back at it (a check-off), `removed` when none does (an
 * item taken off without being bought). Nothing new is written to produce it and
 * no sync invariant is touched, which is the whole reason it can be derived
 * rather than logged (see `db/activity.ts`).
 *
 * It writes nothing at all: emoji reactions (migration 020) were removed on
 * 2026-08-30, so the feed is now purely a read.
 */
/**
 * The verbs, English snake-case like every enum value (API §1). The Dutch a
 * person reads — "toegevoegd", "gekocht", "verwijderd" — lives in the client.
 *
 * The two ways a line leaves the list are distinct and both shown, because the
 * whole point of the feed is to see the other person took the butter off before
 * you buy a second one — and the common way that happens is a check-off, not a
 * delete: `bought` is a tombstoned item with a purchase behind it, `removed` one
 * without. (Until 2026-08-31 check-offs were excluded entirely, which hid all
 * but the rare changed-my-mind delete — the feed's own reason for existing.)
 */
export const ACTIVITY_VERBS = ['added', 'bought', 'removed'];
export const activityVerb = z.enum(ACTIVITY_VERBS);
export const activityEventDto = z.object({
    /**
     * `${verb}:${list_item_id}` — stable and unique per event. A re-add is a new
     * list item with a new id (tombstones are sticky, DATABASE §12), so it is a
     * new event with a new key rather than a resurrected one.
     */
    event_key: z.string(),
    verb: activityVerb,
    /** When it happened: the item's `created_at` for an add, `deleted_at` for a buy or a removal. */
    at: isoTimestamp,
    /** Named, never a device id — the feed says "Sanne", the same as notifications (DATABASE §3). */
    actor: z.object({ id: uuid, display_name: z.string() }),
    product: z.object({
        id: uuid,
        name: z.string(),
        category: z.string().nullable(),
        /**
         * Borrowed from the item's variant, or the last variant of this product the
         * household bought — a generic has no picture of its own.
         * Null falls back to the lettered tile, which is most manual entries.
         */
        image_path: z.string().nullable(),
    }),
    quantity: z.number().int(),
    note: z.string().nullable(),
});
export const activityResponse = z.object({ events: z.array(activityEventDto) });
//# sourceMappingURL=activity.js.map